/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Cell, Row, TableCommonProps } from "react-table";
import { theme, Text, IndeterminateCheckbox } from "@nordcloud/gnui";
import { EnvironmentAndSpends, Provider } from "~/generated/graphql";
import { CostForecastHeader } from "~/components";
import {
  EstateCostCell,
  ProviderCell,
  ResourceNameDetailsCell,
} from "~/components/Tables/Cells";
import { useConsoleUrl } from "~/hooks";
import {
  AccountLink,
  SelectionCell,
  ApplicationEnvironmentCell,
} from "./cells";

type Estate = {
  id: string;
  provider: Provider;
  type?: string;
  category?: string;
  nordcloudId: string;
  cloudAccountId?: string;
  nid: string;
  name: string;
  environment: {
    name: string;
    nid: string;
    application: {
      nid: string;
      name: string;
    };
  };
  environments: EnvironmentAndSpends[];
  actualCosts: string;
  forecast: string;
  region: string;
  isWaste: boolean;
  reason: string;
  savingsAmount: string;
  savingsAmountCount: number;
  co2e: number;
  co2eSavings: number;
  assignments: {
    appCount: number;
    envCount: number;
  };
  count: number;
};

const applicationCell = ({ row }: Cell<Estate>) => (
  <ApplicationEnvironmentCell
    environment={row.original?.environment}
    assignments={row.original.assignments}
  />
);

const selectionHeader = ({
  getToggleAllRowsSelectedProps,
}: {
  getToggleAllRowsSelectedProps: () => TableCommonProps;
}) => <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />;

const selectionCell = ({ row }: Cell<Estate>) => (
  <SelectionCell {...row.getToggleRowSelectedProps()} />
);

export const EstateListColumns = [
  {
    id: "selection",
    accessor: "selection",
    width: "3%",
    disableSortBy: true,
    Header: selectionHeader,
    Cell: (cell: Cell<Estate>) => {
      const { environment } = cell.row.original;

      return environment ? selectionCell(cell) : null;
    },
  },
  {
    Header: "",
    accessor: "provider",
    disableSortBy: true,
    width: "3%",
    Cell: ({ row }: Cell<Estate>) => (
      <ProviderCell provider={row.original.provider} />
    ),
  },
  {
    Header: "Resource name / Resource Details",
    accessor: "nordcloudId",
    disableSortBy: true,
    width: "16rem",
    Cell: ({ row }: Cell<Estate>) => {
      const {
        type,
        cloudAccountId,
        name,
        id,
        nordcloudId,
        provider,
        category,
        region,
      } = row.original;
      const { url } = useConsoleUrl({
        ...row.original,
        cloudAccountId: cloudAccountId ?? "",
      });

      return (
        <ResourceNameDetailsCell
          id={id}
          nordcloudId={nordcloudId}
          name={name}
          type={type}
          category={category}
          url={url}
          provider={provider}
          region={region}
        />
      );
    },
  },
  {
    Header: "Account Name / Account ID",
    accessor: "region",
    disableSortBy: true,
    width: "19rem",
    Cell: ({ row }: Cell<Estate>) => (
      <>
        <AccountLink cloudAccountId={row.original.cloudAccountId} />
        <Text size="sm" tag="div" color={theme.color.text.text02}>
          {row.original.cloudAccountId}
        </Text>
      </>
    ),
  },
  {
    Header: "Application / Environment",
    accessor: "application",
    disableSortBy: true,
    width: "13rem",
    Cell: applicationCell,
  },
  {
    Header: <CostForecastHeader>Monthly Cost</CostForecastHeader>,
    accessor: "actualCosts",
    disableSortBy: true,
    width: "6rem",
    Cell: ({ row }: Cell<Estate>) => {
      const {
        id,
        isWaste,
        reason,
        actualCosts,
        savingsAmount,
        savingsAmountCount,
        co2e,
        co2eSavings,
      } = row.original;
      return (
        <EstateCostCell
          estateId={id}
          isWaste={isWaste}
          reason={reason}
          value={actualCosts}
          amount={savingsAmount}
          count={savingsAmountCount ?? 0}
          co2e={co2e}
          co2eSavings={co2eSavings}
          extraInfo={getExtraInfo(row)}
          costTooltipValue={getCostTooltipValue(row)}
        />
      );
    },
  },
  {
    Header: <CostForecastHeader>Forecast</CostForecastHeader>,
    accessor: "forecastCost",
    disableSortBy: true,
    width: "6rem",
    Cell: ({ row }: Cell<Estate>) => {
      const { id, isWaste, reason, forecast } = row.original;

      return (
        <EstateCostCell
          estateId={id}
          isWaste={isWaste}
          reason={reason}
          value={forecast}
          amount=""
          count={0}
        />
      );
    },
  },
];

function isGcpProject(provider: Provider, type?: string) {
  return provider === Provider.Gcp && type === "project";
}

function getExtraInfo(estate: Row<Estate>) {
  const { count, provider, type } = estate.original;
  return isGcpProject(provider, type) && count > 0 ? `${count} services` : "";
}

function getCostTooltipValue(estate: Row<Estate>) {
  const { provider, type } = estate.original;

  if (!isGcpProject(provider, type)) {
    return;
  }

  return "The cost of the project is highlighted in grey because IBM Multicloud Accelerator takes more granular service-level costs in all calculations. Click the number of services to see more details.";
}
